/**
 * Plays SFX recipes (sfxSpecs.ts) into the SFX bus, tuned to the current theme key.
 */
import type { SfxName } from '../contracts.ts';
import type { MixGraph } from './graph.ts';
import { SFX, resolvePitch } from './sfxSpecs.ts';
import { playNoiseGrain, playVoice } from './synth.ts';

/** Tiny lead time so a note's envelope always starts on a fresh render quantum. */
const START_OFFSET_S = 0.005;
/** Golden-ratio stride through the noise buffer: successive grains never repeat. */
const GRAIN_STRIDE_S = 0.618;

export class SfxPlayer {
  private grainOffset = 0;

  constructor(private readonly g: MixGraph) {}

  /** Play `name` now. `rootMidi` is the SFX root of the current theme key (REGISTER.sfx). */
  play(name: SfxName, rootMidi: number): void {
    const spec = SFX[name];
    const { ctx, sfxIn, noise } = this.g;
    const t0 = ctx.currentTime + START_OFFSET_S;
    for (const layer of spec.layers) {
      if (layer.kind === 'noise') {
        this.grainOffset = (this.grainOffset + GRAIN_STRIDE_S) % noise.white.duration;
        playNoiseGrain(ctx, sfxIn, noise.white, {
          time: t0 + layer.at,
          dur: layer.dur,
          gain: layer.gain,
          attack: layer.attack,
          filter: layer.filter,
          offsetS: this.grainOffset,
        });
        continue;
      }
      for (const n of layer.notes) {
        playVoice(
          ctx,
          sfxIn,
          layer.voice,
          {
            hz: resolvePitch(n.pitch, rootMidi),
            time: t0 + n.at,
            dur: n.dur,
            velocity: n.velocity,
            glideToHz: n.glideTo ? resolvePitch(n.glideTo, rootMidi) : undefined,
            glideS: n.glideS,
          },
          noise.white,
        );
      }
    }
  }
}
